import * as React from 'react'
import { styled, css } from 'next-yak'

import * as COLORS from '../../constants/colors'
import * as button from '../../styles/button'
import * as spinner from '../../styles/spinner'
import { Spinner } from '../Spinner'
import { useTransition } from '../../behaviours'
import { PolymorphicComponent, YakComponentOf } from '@owl-nest/yak/extra-runtime'

type ExtraButtonProps = button.ButtonProps & {
  /** display the button in loading state (disabled with spinner instead of label) */
  loading?: boolean
}

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & ExtraButtonProps

function ButtonComponent({
  children,
  loading = false,
  disabled,
  ...props
}: ButtonProps): React.ReactElement<ButtonProps> {
  const { step, unmounted, onTransitionEnd } = useTransition(loading)

  const validatedColor = props.color && props.color in button.buttonColors ? props.color : undefined

  return (
    <ButtonWrapper
      {...props}
      color={validatedColor}
      disabled={loading || disabled}
      loading={loading}
      $step={step}
      $unmounted={unmounted}
    >
      <button.Label>{children}</button.Label> <Spinner onTransitionEnd={onTransitionEnd} />
    </ButtonWrapper>
  )
}

type ButtonWrapperProps = button.ButtonProps & { $step: 'before' | 'during' | 'after'; $unmounted: boolean }

const ButtonWrapper = styled(button.Button)<ButtonWrapperProps>`
  position: relative;

  ${spinner.Spinner} {
    left: 50%;
    opacity: ${({ $step, $unmounted }) => ($step === 'during' && !$unmounted ? 1 : 0)};
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    transition: opacity 0.3s ease;
  }

  ${button.Label} {
    transition: opacity 0.3s ease;
    opacity: ${({ $step, $unmounted }) => ($step !== 'during' || $unmounted ? 1 : 0)};
  }

  ${spinner.Dot} {
    ${({ kind = 'primary' }) => {
      if (kind === 'primary') {
        return css`
          background-color: ${COLORS.PRIMARY_GREY_000};
        `
      }
      return css`
        background-color: ${({ color }) => button.getPrimaryColor(color)};
      `
    }}
  }
` as YakComponentOf<PolymorphicComponent<'button', ButtonWrapperProps>>

export const Button = styled(ButtonComponent)<ButtonProps>`` as YakComponentOf<
  PolymorphicComponent<'button', ExtraButtonProps>
>
Button.displayName = 'Button'
